import React from 'react';
import { SelectDropDown } from 'components/common/Elements/Elements';
import { mapStatusOptions } from '../../helpers/helpers';
import { optionStatus, featureFlagsStatuses } from '../../helpers/configurates';
import useStyles from './perRegion.styles';

export interface RegionRow {
  region: string;
  exists: boolean;
  originalStatus: string | null;
  customerIdsCount: number;
  draftStatus: string | null;
  statusUnknown?: boolean;
}

export interface RowResult {
  ok: boolean;
  statusCode?: number;
  failureKind?: 'clear' | 'ambiguous';
}

interface Props {
  row: RegionRow;
  t: (key: string, opts?: Record<string, unknown>) => string;
  disabled: boolean;
  result?: RowResult | null;
  onStatusChange: (region: string, status: string) => void;
  onRetry?: (region: string) => void;
}

const RegionStatusRow: React.FC<Props> = ({
  row,
  t,
  disabled,
  result,
  onStatusChange,
  onRetry
}) => {
  const classes = useStyles();
  const {
    region,
    exists,
    originalStatus,
    customerIdsCount,
    draftStatus,
    statusUnknown
  } = row;

  const options = exists
    ? mapStatusOptions(draftStatus || originalStatus, customerIdsCount, t)
    : optionStatus;
  const changed = draftStatus !== originalStatus;
  const fromLabel =
    originalStatus === null
      ? t('featureFlags:NOT_SET')
      : t(`featureFlags:${originalStatus}`);
  const leavingGA =
    originalStatus === featureFlagsStatuses.GlobalAssigned &&
    draftStatus !== featureFlagsStatuses.GlobalAssigned;

  const successLabel = () => {
    if (!exists || originalStatus === null) {
      return t('featureFlags:RESULT_CREATED');
    }
    if (leavingGA) {
      return t('featureFlags:RESULT_UPDATED_UNASSIGNED');
    }
    if (
      draftStatus === featureFlagsStatuses.GlobalAssigned &&
      originalStatus !== featureFlagsStatuses.GlobalAssigned
    ) {
      return t('featureFlags:RESULT_UPDATED_ASSIGNED');
    }
    return t('featureFlags:RESULT_UPDATED');
  };

  const renderCaption = () => {
    if (statusUnknown) {
      return (
        <p className={`${classes.caption} ${classes.captionError}`}>
          {t('featureFlags:REGION_STATUS_UNAVAILABLE')}
        </p>
      );
    }
    if (result) {
      if (result.ok) {
        return (
          <p className={`${classes.caption} ${classes.captionSuccess}`}>
            {successLabel()}
          </p>
        );
      }
      if (result.failureKind === 'ambiguous') {
        return (
          <p className={`${classes.caption} ${classes.captionAmber}`}>
            {t('featureFlags:RESULT_AMBIGUOUS')}
          </p>
        );
      }
      return (
        <p className={`${classes.caption} ${classes.captionError}`}>
          {result.failureKind === 'clear'
            ? t('featureFlags:RESULT_CLEAR_FAIL', { status: fromLabel })
            : t('featureFlags:RESULT_FAIL')}{' '}
          {onRetry && (
            <button
              type="button"
              className={classes.linkBtn}
              onClick={() => onRetry(region)}
            >
              {t('featureFlags:RETRY')}
            </button>
          )}
        </p>
      );
    }
    if (changed) {
      return (
        <p className={`${classes.caption} ${classes.captionInfo}`}>
          {leavingGA
            ? t('featureFlags:CHANGED_FROM_WILL_UNASSIGN', {
                status: fromLabel
              })
            : t('featureFlags:CHANGED_FROM', { status: fromLabel })}
        </p>
      );
    }
    return null;
  };

  return (
    <div>
      <div className={classes.row}>
        <span className={classes.regionLabel}>{region}</span>
        <div className={classes.control}>
          <SelectDropDown
            clearable={false}
            reload
            className={classes.dropdown}
            options={options}
            renderKey="label"
            placeholder={t('featureFlags:NOT_SET')}
            isDisabled={disabled}
            input={{
              value: draftStatus || '',
              onChange: (val: string) => onStatusChange(region, val)
            }}
          />
        </div>
      </div>
      {renderCaption()}
    </div>
  );
};

export default RegionStatusRow;
